import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import useErrorModalStore from '../store/useErrorModalStore';
import { getErrorModalMessage } from '../utils/errorMessages';
import { isAfter, differenceInSeconds, addMinutes } from 'date-fns';
import usePostErrorStore from '../store/usePostErrorStore';

/**
 * Poll for profile completion status
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether polling should be active
 */
export const useProfilePoll = (accessKey, enabled) => {
  return useQuery({
    queryKey: ['profile-status', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileReady(accessKey);
      return result;
    },
    enabled: enabled && !!accessKey,
    refetchInterval: (data) => {
      // Stop polling if profile is created
      if (data?.status === 'created') {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    cacheTime: 0,
    staleTime: 0,
    refetchIntervalInBackground: true,
    retry: 3,
  });
};

/**
 * Poll for offerings completion status
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether polling should be active
 */
export const useOfferingsPoll = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['offerings-status', accessKey],
    queryFn: async () => {
      const result = await api.checkOfferingsReady(accessKey);
      return result;
    },
    enabled: enabled && !!accessKey,
    refetchInterval: (data) => {
      // Stop polling if offerings are ready (array has items)
      if (data && Array.isArray(data) && data.length > 0) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 1,
  });
};

const POLL_INTERVAL = 3000; // 3 seconds

/**
 * Poll for posts completion status with configurable timeout
 * @param {boolean} enabled - Whether polling should be active
 * @param {string} context - Context of polling ('onboarding' or 'dashboard')
 * @param {function} onSuccess - Callback when posts are fetched successfully
 * @param {function} onError - Callback when fetching posts fails
 */
export const usePostsPoll = (enabled = false, context = 'dashboard', onSuccess, onError) => {
  const startTimeRef = React.useRef(null);
  const shouldTimeoutRef = React.useRef(false);
  const { openErrorModal, isOpen } = useErrorModalStore();
  const { setPostError, postErrors } = usePostErrorStore();
  const queryClient = useQueryClient();
  // Reset refs when query is disabled or context changes
  React.useEffect(() => {
    if (!enabled) {
      startTimeRef.current = null;
      shouldTimeoutRef.current = null;
    } else {
      // Reset timer when context changes while enabled
      startTimeRef.current = null;
      shouldTimeoutRef.current = null;
    }
    return () => {
      startTimeRef.current = null;
      shouldTimeoutRef.current = null;
    };
  }, [enabled, context]);

  return useQuery({
    queryKey: ['posts-status', context],
    queryFn: async () => {
      try {
        if (!startTimeRef.current) {
          startTimeRef.current = Date.now();
        }
        const result = await api.checkPostsReady();
        const deadline = addMinutes(startTimeRef.current, 3);
        const shouldTimeout = isAfter(new Date(), deadline);
        const hasData = result && Array.isArray(result) && result.length > 0;
        // Check if we have the completion state (both story and feed posts, no drafts)
        const hasStoryPost =
          hasData &&
          result.some((post) => post.status === 'pending' && post.aspectRatio === '9:16');
        const hasFeedPost =
          hasData && result.some((post) => post.status === 'pending' && post.aspectRatio === '1:1');
        const hasDraftPosts = hasData && result.some((post) => post.status === 'draft');
        const draftPosts = hasData && result.filter((post) => post.status === 'draft');
        const hasCompletionState =
          hasStoryPost && hasFeedPost && context === 'onboarding'
            ? true
            : hasStoryPost && hasFeedPost && !hasDraftPosts;

        // Reset timer when completion state is reached
        if (hasCompletionState) {
          startTimeRef.current = null;
          queryClient.invalidateQueries({ queryKey: ['posts'] }); // Ensure we fetch the latest posts data on completion
        }

        // Timeout if we've been polling for too long without reaching completion state

        if (!hasCompletionState && shouldTimeout) {
          if (hasDraftPosts) {
            draftPosts.forEach((post) => {
              setPostError(
                post.id,
                'This post is taking longer than expected to generate. Please try regenerating or contact support if the issue persists.'
              );
            });
            return result; // Return data even on timeout to allow UI to update with any completed posts
          }
          openErrorModal(
            getErrorModalMessage('dashboard_posts_timeout'),
            'dashboard_posts_timeout'
          );
          const error = new Error(
            context === 'onboarding'
              ? 'Post generation is taking longer than expected. Proceeding to dashboard.'
              : 'Failed to load posts. Please refresh the page or contact support.'
          );
          error.code =
            context === 'onboarding' ? 'ONBOARDING_POSTS_TIMEOUT' : 'DASHBOARD_POSTS_TIMEOUT';
          error.context = context;
          error.elapsedTime = shouldTimeout
            ? differenceInSeconds(new Date(), startTimeRef.current)
            : null;
          throw error;
        }
        return result;
      } catch (error) {
        if (error?.response?.status >= 500) {
          openErrorModal(
            getErrorModalMessage(
              context === 'onboarding' ? 'onboarding_posts_error' : 'dashboard_posts_error'
            ),
            context === 'onboarding' ? 'onboarding_posts_error' : 'dashboard_posts_error'
          );
        }
        throw error;
      }
    },
    enabled: enabled,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      if (onError) onError(error);
    },
    refetchInterval: (data) => {
      if (postErrors && Object.keys(postErrors).length > 0) {
        return false; // Stop polling if there are any post errors
      }
      // Stop polling if timeout error occurred
      if (isOpen) {
        return false;
      }

      if (!data || !Array.isArray(data)) {
        return POLL_INTERVAL;
      }

      // Check if we have both story (9:16) and feed (1:1) posts with pending status
      const hasStoryPost = data.some(
        (post) => post.status === 'pending' && post.aspectRatio === '9:16'
      );
      const hasFeedPost = data.some(
        (post) => post.status === 'pending' && post.aspectRatio === '1:1'
      );
      const hasDraftPosts = data.some((post) => post.status === 'draft');

      // Stop polling when we have both types of posts and no draft posts
      if (hasStoryPost && hasFeedPost) {
        if (context === 'onboarding') {
          startTimeRef.current = null; // Reset timer on completion
          return false; // Stop polling if we have the required posts for onboarding context, even if there are draft posts (since drafts don't block onboarding flow)
        }
        if (context === 'dashboard' && hasDraftPosts) {
          return POLL_INTERVAL; // Stop polling if we have the required posts and no drafts for dashboard context
        }

        startTimeRef.current = null;

        return false;
      }

      return POLL_INTERVAL; // Continue polling every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 2,
    cacheTime: 30000,
    staleTime: 0,
  });
};

export const useGetSummeryPoll = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['profile-summary', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileSummaryReady(accessKey);
      return result;
    },
    enabled: enabled && !!accessKey,
    refetchInterval: (data) => {
      // Stop polling if summary is ready
      if (data && data.summary_ready) {
        return false;
      }
      return 3000; // Poll every 3 seconds
    },
    refetchIntervalInBackground: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useGetProfileSummary = (accessKey, enabled = false, onSuccess, onError) => {
  return useQuery({
    queryKey: ['profile-summary', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileSummaryReady(accessKey);
      return result;
    },
    enabled: enabled && !!accessKey,
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      console.error('Profile summary fetch error:', error);
      if (onError) onError(error);
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Get offerings (single request, no polling)
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether query should be active
 */
export const useGetOfferings = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['offerings', accessKey],
    queryFn: async () => {
      const result = await api.checkOfferingsReady(accessKey);
      return result;
    },
    enabled: enabled && !!accessKey,
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};

/**
 * Get summary (single request, no polling)
 * @param {string} accessKey - User's access key
 * @param {boolean} enabled - Whether query should be active
 */
export const useGetSummary = (accessKey, enabled = false) => {
  return useQuery({
    queryKey: ['summary', accessKey],
    queryFn: async () => {
      const result = await api.checkProfileSummaryReady(accessKey);
      return result;
    },
    enabled: enabled && !!accessKey,
    retry: 2,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
};
